import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient, keepPreviousData, QueryKey } from '@tanstack/react-query';
import type { Contract, ContractsResponse, ContractStatusTotals } from '@app-types';

interface UseContractPageDataOptions {
  queryKey: QueryKey;
  queryFn: () => Promise<ContractsResponse>;
  pageSize: number; // Required for correct totalPages calculation
  enabled?: boolean;
}

export interface UseContractPageDataReturn {
  // Data
  contracts: Contract[];
  isLoading: boolean;
  error: Error | null;
  isFetching: boolean;
  
  // Response
  response: ContractsResponse | undefined;
  
  // Status totals
  filterCounts: Record<string, number>;

  // Reassessment counts (separate from status filter counts)
  reassessmentCounts: { reassessmentDue: number; readyForScheduling: number };
  
  // Pagination
  totalItems: number;
  totalPages: number;
  
  // Actions
  refreshData: () => void;
}

/**
 * Generic hook for data fetching in contract pages
 * Handles: data fetching, statusTotals preservation, filter counts, pagination
 * Same implementation as useSchedulePageData and useAssessmentPageData
 */
export function useContractPageData(options: UseContractPageDataOptions): UseContractPageDataReturn {
  const { queryKey, queryFn, pageSize, enabled = true } = options;
  const queryClient = useQueryClient();

  // Fetch data with placeholderData to prevent UI flicker
  const { data: response, isLoading, error, isFetching } = useQuery<ContractsResponse>({
    queryKey,
    queryFn,
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: keepPreviousData,
    enabled,
  });

  // Extract contracts
  const contracts = useMemo(() => response?.data || [], [response]);

  // Preserve last known totals to avoid counter flicker between filter changes
  const [lastStatusTotals, setLastStatusTotals] = useState<ContractStatusTotals | null>(null);
  useEffect(() => {
    if (response?.statusTotals) {
      setLastStatusTotals(response.statusTotals);
    }
  }, [response?.statusTotals]);

  // Calculate filter counts from statusTotals only (reassessment counts are separate)
  const filterCounts: Record<string, number> = useMemo(() => {
    const totals = response?.statusTotals || lastStatusTotals;

    if (!totals) {
      return {
        all: response?.total || 0,
        Active: 0,
        Inactive: 0,
        unassigned: 0,
      };
    }

    // Calculate 'all' count based on CMS status fields only
    const statusValues = [
      totals.Active || 0,
      totals.Inactive || 0,
    ];

    const allFromTotals = statusValues.reduce((sum, val) => sum + val, 0);

    return {
      all: allFromTotals || response?.total || 0,
      Active: totals.Active || 0,
      Inactive: totals.Inactive || 0,
      unassigned: totals.unassigned || 0,
    };
  }, [response, lastStatusTotals]);

  // Reassessment counts exposed separately for the inline column filter
  const reassessmentCounts = useMemo(
    () => response?.reassessmentCounts ?? { reassessmentDue: 0, readyForScheduling: 0 },
    [response]
  );

  // Pagination calculations
  const totalItems = response?.total || 0;
  const totalPages = Math.ceil(totalItems / (pageSize || 1));

  // Refresh data callback
  const refreshData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [queryKey[0]] });
  }, [queryClient, queryKey]);

  return {
    // Data
    contracts,
    isLoading,
    error: error as Error | null,
    isFetching,
    
    // Response
    response,
    
    // Status totals
    filterCounts,

    // Reassessment counts
    reassessmentCounts,
    
    // Pagination
    totalItems,
    totalPages,
    
    // Actions
    refreshData,
  };
}
